import { Component, Inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { cloneDeep, includes, reduce } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IDropdownOption } from 'Src/ng2/shared/typings/interfaces/design-library.interface';
import {
  BulkUpdateStudentAssessments,
  IStudentAssessmentUpdateOption,
} from 'Src/ng2/store/actions/assessments-actions/student-assessments-actions';
import { unsubscribeComponent } from '../../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ModalsService } from '../../../../shared/modals/modals.service';
import { UtilitiesService } from '../../../../shared/services/utilities/utilities.service';
import { ISchoolAssessment } from '../../../../shared/typings/interfaces/school-assessment.interface';
import { IStudentAssessment } from '../../../../shared/typings/interfaces/student-assessment.interface';
import { AssessmentsDataInputService } from './assessments-data-input.service';

export interface IAssessmentInputRow {
  studentAssessmentId: string;
  studentId: string;
  name: string;
  instructionalScore: string;
  independentScore: string;
  assessmentDate: string;
  lastEdited: string;
}

interface IAssessmentPayloadItem {
  section: string;
  value: string;
}

interface IAssessmentPayload {
  studentAssessmentId: string;
  schoolAssessmentId: string;
  items: IAssessmentPayloadItem[];
  assessedOn: string;
  status: 'COMPLETED' | 'INCOMPLETE';
}

@unsubscribeComponent
@Component({
  selector: 'assessments-data-input',
  template: require('./assessments-data-input.component.html'),
  styles: [require('./assessments-data-input.component.scss')],
})
export class AssessmentsDataInputComponent implements OnInit {
  @Input() schoolAssessments$: Observable<ISchoolAssessment[]>;
  @Input() studentAssessments$: Observable<IStudentAssessment[]>;
  public schoolAssessmentId: string;
  public schoolAssessment: ISchoolAssessment;
  public validInstructionalScores: {};
  public validIndependentScores: {};
  public studentAssessments: IStudentAssessment[];
  public showShadow: boolean;
  public filterForm: FormControl;
  public assessmentsForm: FormGroup;
  public assessmentsFormSub: Subscription;
  public stuAndSchAssmntsSubs: Subscription;
  gradeGroupings: {
    [name: string]: {
      rowData: IAssessmentInputRow[];
    };
  };
  classGroupings: {
    [name: string]: {
      rowData: IAssessmentInputRow[];
    };
  };
  currentGroupingData$: BehaviorSubject<IAssessmentInputRow[]>;
  currentGroupingCount: number;
  groupingOptions: IDropdownOption[];
  selectedGrouping: IDropdownOption;
  sort$: BehaviorSubject<{ key: string; dir: string }>;
  originalFormControls: { [key: string]: FormGroup };

  constructor(
    @Inject('$stateParams') private $stateParams: any,
    @Inject('$state') private $state,
    private utilitiesService: UtilitiesService,
    private assmntsDataInputSrv: AssessmentsDataInputService,
    private modalsService: ModalsService,
    private store: Store<any>,
  ) {
    //
  }

  ngOnInit() {
    this.currentGroupingData$ = new BehaviorSubject([]);
    this.schoolAssessmentId = this.$stateParams.schoolAssessmentId;

    // get school and student assessments for selected admin from the store (JE)
    this.stuAndSchAssmntsSubs = combineLatest(this.schoolAssessments$, this.studentAssessments$)
      .pipe(
        tap(([schoolAssessments, studentAssessments]) => {
          this.schoolAssessment = schoolAssessments.find(
            schoolAssessment => schoolAssessment._id === this.schoolAssessmentId,
          );
          this.validInstructionalScores = this.schoolAssessment.assessment.items[0].validValues.reduce(
            (a, b) => ((a[b] = ''), a),
            {},
          );
          this.validIndependentScores = this.schoolAssessment.assessment.items[0].validValues.reduce(
            (a, b) => ((a[b] = ''), a),
            {},
          );

          this.studentAssessments = studentAssessments.filter(asmnt => {
            const {
              schoolAssessment: { adminNumber, assessmentNickName },
            } = asmnt;
            return (
              adminNumber === this.schoolAssessment.adminNumber &&
              assessmentNickName === this.schoolAssessment.assessment.assessmentNickName
            );
          });

          // get grouped rowData and dropdown options all at once - jchu
          const {
            Grade: { groupedAssessments: gradeGroupings, options: gradeOptions },
            Class: { groupedAssessments: classGroupings, options: classOptions },
          } = this.assmntsDataInputSrv.getAllGrouping(this.studentAssessments);
          this.gradeGroupings = gradeGroupings;
          this.classGroupings = classGroupings;
          this.groupingOptions = this.assmntsDataInputSrv.reshapeGroupingOptions({
            classOptions,
            gradeOptions,
          });
          const prevOption = this.selectedGrouping;
          const defaultOption = this.groupingOptions[0].options[0];
          this.updateGrouping({ option: prevOption || defaultOption, source: 'store' });
        }),
      )
      .subscribe();
  }

  updateGrouping({ option, source }) {
    // first load or after form saved - jchu
    if (source === 'store') {
      this.initControls();
      this.setGrouping(option, source);
    } else if (source === 'dropdown') {
      // group option changed (pill dropdown from child) - jchu
      if (this.selectedGrouping && this.selectedGrouping.human === option.human) {
        return;
      } else {
        if (this.assessmentsForm && this.assessmentsForm.dirty) {
          const data = {
            title: 'Are you sure you want to leave the page?',
            message: 'You will lose any unsaved changes',
            confirmText: 'Yes',
          };
          this.modalsService
            .openConfirmModal(data, 'med')
            .afterClosed()
            .subscribe((confirmed: false | true) => {
              if (confirmed) {
                this.setGrouping(option, source);
              }
            });
        } else {
          this.setGrouping(option, source);
        }
      }
    }
  }

  initControls() {
    if (this.filterForm) {
      this.filterForm.setValue(this.filterForm.value);
    } else {
      this.filterForm = new FormControl('');
    }
    if (this.sort$) {
      this.sort$.next(this.sort$.value);
    } else {
      this.sort$ = new BehaviorSubject({ dir: 'asc', key: 'name' });
    }
  }

  setGrouping(option: IDropdownOption, source: string = '') {
    this.selectedGrouping = option;
    const groupingObj = this.gradeGroupings[option.human] || this.classGroupings[option.human];
    this.currentGroupingData$.next(groupingObj.rowData);
    this._setsAssessmentsForm(groupingObj.rowData, source);
  }

  // TODO: make this dynamic once more assessments are added (JE)
  _setsAssessmentsForm(rowData: IAssessmentInputRow[], source: string = ''): void {
    let assessmentsForm = new FormGroup({});
    if (!this.assessmentsForm || source === 'dropdown') {
      const {
        assessment: { items },
      } = this.schoolAssessment;
      const validInstructionalScores = items[0].validValues;
      const validIndependentScores = items[1].validValues;
      rowData.forEach(row => {
        const instScore = row.instructionalScore ? row.instructionalScore : '';
        const indScore = row.independentScore ? row.independentScore : '';
        const rowForm = new FormGroup({
          instructional: new FormControl(instScore, [
            Validators.required,
            this.optionsValidator(validInstructionalScores),
          ]),
          independent: new FormControl(indScore, [Validators.required, this.optionsValidator(validIndependentScores)]),
          assessmentDate: new FormControl(row.assessmentDate, Validators.required),
          studentAssessmentId: new FormControl(row.studentAssessmentId),
        });
        assessmentsForm.registerControl(`${row.studentId}`, rowForm);
      });
      this.assessmentsForm = assessmentsForm;
      this.originalFormControls = cloneDeep(this.assessmentsForm.controls) as { [key: string]: FormGroup };
    } else {
      rowData.forEach(row => {
        const instScore = row.instructionalScore ? row.instructionalScore : '';
        const indScore = row.independentScore ? row.independentScore : '';
        const asmtDate = row.assessmentDate ? row.assessmentDate : null;
        (this.assessmentsForm as any).controls[row.studentId].controls.independent.value = indScore;
        (this.assessmentsForm as any).controls[row.studentId].controls.instructional.value = instScore;
        (this.assessmentsForm as any).controls[row.studentId].controls.assessmentDate.value = asmtDate;
      });
      this.assessmentsForm = cloneDeep(this.assessmentsForm);
    }
  }

  // a custom validator to restrict inputs to the list of valid values specific to each assessment item (JE)
  optionsValidator(validOptions: string[]): ValidatorFn {
    return (control: AbstractControl) => {
      const upperCased = control.value ? control.value.toUpperCase() : null;
      const match = validOptions.find(option => includes(option.toUpperCase(), upperCased));
      if (match) {
        return null;
      } else {
        if (this.assessmentsForm && control && control.value.length) {
          const trimmedValue = control.value.substring(0, control.value.length - 1);
          control.setValue(trimmedValue);
          return !control.value.length ? { invalid: true } : null;
        } else {
          return { invalid: true };
        }
      }
    };
  }

  // validates the payload once a user has submitted the form
  validatePayload(payload: IAssessmentPayload): IAssessmentPayload {
    if (!payload || !this.validInstructionalScores || !this.validIndependentScores) return payload;

    const instructScore = payload.items[1].value;
    const independentScore = payload.items[0].value;

    const isValidInstructionalScore = instructScore !== null ? this.validInstructionalScores[instructScore] : true;
    const isValidIndependentScore = independentScore !== null ? this.validIndependentScores[independentScore] : true;

    if (isValidInstructionalScore === undefined && isValidIndependentScore === undefined) {
      delete payload.items;
    } else {
      if (isValidInstructionalScore === undefined) payload.items[1].value = null;
      if (isValidIndependentScore === undefined) payload.items[0].value = null;
    }
    return payload;
  }

  // Validates data entry input values (checks for things like 'pr', 're, etc)
  validateInput({ instructScore, independentScore }): { instructScore: string; independentScore: string } {
    if (
      instructScore !== undefined &&
      independentScore !== undefined &&
      this.validInstructionalScores &&
      this.validIndependentScores
    ) {
      const isValidInstructionalScore = this.validInstructionalScores[instructScore.toUpperCase()];
      const isValidIndependentScore = this.validIndependentScores[independentScore.toUpperCase()];

      if (isValidInstructionalScore === undefined) instructScore = null;
      if (isValidIndependentScore === undefined) independentScore = null;
    }
    return {
      instructScore,
      independentScore,
    };
  }

  checkHeaderShadow(element: HTMLElement) {
    this.showShadow = this.utilitiesService.showListPanelShadow(element.scrollTop);
  }

  goToStudentProfile(studentId: string): void {
    if (this.assessmentsForm && this.assessmentsForm.dirty) {
      const data = {
        title: 'Are you sure you want to leave the page?',
        message: 'You will lose any unsaved changes',
        confirmText: 'Yes',
      };
      this.modalsService
        .openConfirmModal(data, 'med')
        .afterClosed()
        .subscribe((confirmed: false | true) => {
          if (confirmed) {
            this.$state.go('main.school.single-student', { studentId });
          }
        });
    } else {
      this.$state.go('main.school.single-student', { studentId });
    }
  }

  // TODO: this is currently specific to the F & P inputs, update it to be
  // dynamic once other assessments are added (JE)
  saveForm(): void {
    const payloadsByGroup: IStudentAssessmentUpdateOption[] = reduce(
      this.assessmentsForm.controls,
      (result, row: FormGroup, groupKey) => {
        if (row.dirty) {
          const { instructional, independent, assessmentDate, studentAssessmentId } = row.controls;

          let instructScore = instructional.value;
          let independentScore = independent.value;

          let payload: any = {
            studentAssessmentId: studentAssessmentId.value,
            schoolAssessmentId: this.schoolAssessmentId,
          };
          if (instructional.dirty || independent.dirty) {
            const ind = this.assmntsDataInputSrv.scoreCasing(independent.value);
            const inst = this.assmntsDataInputSrv.scoreCasing(instructional.value);
            const items = [
              { section: 'Independent Score', value: ind },
              { section: 'Instructional Score', value: inst },
            ];
            payload.items = items;

            const validatedPayload: IAssessmentPayload = this.validatePayload(payload);
            payload = validatedPayload;

            ({ instructScore, independentScore } = this.validateInput({ instructScore, independentScore }));
          }
          if (assessmentDate.dirty) {
            payload.assessedOn = assessmentDate.value;
          }
          const dateAssigned = !!payload.assessedOn || !!assessmentDate.value;
          if (instructScore && independentScore && dateAssigned) payload.status = 'COMPLETED';
          else payload.status = 'INCOMPLETE';
          result.push(payload);
        }
        return result;
      },
      [],
    );
    this.store.dispatch(new BulkUpdateStudentAssessments({ payload: payloadsByGroup }));
    this.assessmentsForm.markAsPristine();
  }

  updateRowDataCount(count: number): void {
    this.currentGroupingCount = count;
  }

  updateSorting(sortKey: string): void {
    if (this.sort$.value.dir === 'asc') {
      this.sort$.next({ dir: 'desc', key: sortKey });
    } else {
      this.sort$.next({ dir: 'asc', key: sortKey });
    }
  }
}
